import { jsx as _jsx } from "react/jsx-runtime";
import { memo, useCallback, useContext, useEffect, useMemo, useState, } from 'react';
import { useAction, useAppSelector, usePartialAppSelector, } from 'shared/hooks/redux/hooks';
import { is_dashboard_sorting_mode, is_grouping_mode, is_sorting_mode, select_ui_settings, set_ui_settings, } from 'console/redux/settings-actions';
import { requestDeviceUpdate } from 'console/redux/devices/items/actions';
import { DeviceUISettingsContent } from './types';
import { DefaultTabKey } from '../../content/tab/types';
import { DeviceUISettingsControlProvider } from './control';
import { getCustomPanels, getPanelsConfig } from '../../content/panels';
import { applyFilterUiSettings, getDeviceUiSettings } from '../../content/ui-settings';
import { TabPanel } from '../../components/tab/panel';
import { PanelsKeys, } from '../../content/panels/types';
import { sortByUISettings } from '../../content/ui-settings/utils';
import { BoilersHeatingCircuits } from '../../components/panels/boilers-heating-circuits';
import { CustomPanel } from '../../components/panels/custom';
import { CustomControls } from '../../components/panels/custom-controls';
import { GuardControl } from '../../components/panels/guard-control';
import { GuardZones } from '../../components/panels/guard-zones';
import { HeatingCircuits } from '../../components/panels/heating-circuits';
import { HeatingModes } from '../../components/panels/heating-modes';
import { RfIndicators } from '../../components/panels/rf-indicators';
import { SimpleViewPanel } from '../../components/panels/simple-view';
import { Thermometers } from '../../components/panels/thermometers';
import { VehiclePanel } from '../../components/panels/vehicle-panel';
import { RfIndicatorsPanel } from '../../components/tab/panel/rf-indicators';
import { Sensors } from '../../components/panels/sensors';
import { useDeviceConfig } from '../device/config';
import { useSupportAccount } from 'console/shared/hooks/helpers';
import { selectDevice, selectSelectedDevice } from 'entities/device/lib';
import { useBooleanState } from 'shared/hooks/state/boolean';
import { saveToLocalStorage } from 'shared/local-storage';
const DefaultHeatingCircuitsTemplate = TabPanel(HeatingCircuits);
const DefaultGuardZonesTemplate = TabPanel(GuardZones);
const DefaultVehiclePanelTemplate = TabPanel(VehiclePanel, { can_hide: false });
const DefaultHeatingModesTemplate = TabPanel(HeatingModes);
const DefaultThermometersTemplate = TabPanel(Thermometers);
const DefaultSensorsTemplate = TabPanel(Sensors);
const DefaultCustomControlsTemplate = TabPanel(CustomControls);
const DefaultBoilerHeatingCircuitsTemplate = TabPanel(BoilersHeatingCircuits);
const DefaultGuardControlTemplate = TabPanel(GuardControl);
const DefaultRfIndicatorsTemplate = RfIndicatorsPanel(RfIndicators);
import * as lodash from 'lodash';
import { removeNulls } from 'utils';
//custom panels - maybeee future feature....
export const DeviceUISettingsProvider = memo(function UISettingsProvider(props) {
    const { children, templates = {}, device_config } = props;
    const { HeatingCircuitsTemplate = DefaultHeatingCircuitsTemplate, BoilerHeatingCircuitsTemplate = DefaultBoilerHeatingCircuitsTemplate, HeatingModesTemplate = DefaultHeatingModesTemplate, GuardZonesTemplate = DefaultGuardZonesTemplate, GuardControlTemplate = DefaultGuardControlTemplate, CustomControlsTemplate = DefaultCustomControlsTemplate, VehiclePanelTemplate = DefaultVehiclePanelTemplate, ThermometersTemplate = DefaultThermometersTemplate, SensorsTemplate = DefaultSensorsTemplate, RfIndicatorsTemplate = DefaultRfIndicatorsTemplate, } = templates;
    const { device_id, device_ui_settings: initial_device_ui_settings, is_climatic, tab_key, is_vehicle_device, } = useDeviceConfig();
    const is_support = useSupportAccount();
    const device = usePartialAppSelector(selectDevice, device_id);
    const is_heat_tab = tab_key === DefaultTabKey.heat_tab;
    const is_custom_tab = tab_key?.toString().includes('custom_tab') ?? false;
    const custom_tab_id = is_custom_tab ? +tab_key.toString().slice(11) : null;
    const owner_user_id = useAppSelector((state) => selectSelectedDevice(state)?.user_id);
    const user_id = useAppSelector((state) => state.account.id);
    const hide_panel_titles = is_heat_tab
        ? device?.hide_titles_on_heat_tab ?? false
        : is_custom_tab
            ? device?.z3k_config?.ui_groups?.find((group) => group.id === custom_tab_id)
                ?.hide_titles ?? false
            : false;
    const dashboard_sorting_mode = useAppSelector(is_dashboard_sorting_mode);
    const grouping_mode = useAppSelector(is_grouping_mode);
    const sorting_mode = usePartialAppSelector(is_sorting_mode, device_id);
    const [is_device_ui_settings_changed, setIsDeviceUiSettingsCancel, setIsDeviceUiSettingsChanged,] = useBooleanState(false);
    const [custom_panels, setCustomPanels] = useState({});
    const user_ui_settings = useAppSelector(select_ui_settings);
    const simple_view_mode = useMemo(() => user_ui_settings.simple_view_mode?.[device_id]?.[tab_key]?.enabled, [user_ui_settings.simple_view_mode, tab_key, device_id]);
    const full_device_config = useMemo(() => ({
        ...device_config,
        terminal: [{ id: 99999, name: 'Терминал' }],
    }), [device_config]);
    const tab_custom_panels = useCallback((panels_config, custom_panels) => {
        const custom_panels_config = getCustomPanels(panels_config, custom_panels);
        return Object.entries(custom_panels_config).map(([panel_key, panel]) => ({
            id: panel_key,
            component: (_jsx(CustomPanel, { name: panel.name, panel_key: panel_key, config: panel.content }, panel_key)),
        }));
    }, []);
    const vehicle_controls_panel = useCallback((vehicle_controls, thermometers, custom_controls) => {
        if (!is_vehicle_device) {
            return null;
        }
        return {
            id: PanelsKeys.vehicle_controls,
            component: (_jsx(VehiclePanelTemplate, { panel_key: PanelsKeys.vehicle_controls, config: vehicle_controls, thermometers: thermometers, custom_controls: custom_controls })),
        };
    }, [is_vehicle_device]);
    const guard_zones_panel = useCallback((guard_zones) => guard_zones && {
        id: PanelsKeys.guard_zones,
        component: (_jsx(GuardZonesTemplate, { panel_key: PanelsKeys.guard_zones, config: guard_zones }, PanelsKeys.guard_zones)),
    }, []);
    const heating_circuits_panel = useCallback((heating_circuits) => heating_circuits && {
        id: PanelsKeys.heating_circuits,
        component: (_jsx(HeatingCircuitsTemplate, { panel_key: PanelsKeys.heating_circuits, config: heating_circuits, hide_title: hide_panel_titles }, PanelsKeys.heating_circuits)),
    }, [hide_panel_titles]);
    const boiler_heating_circuits_panel = useCallback((boiler_heating_circuits) => boiler_heating_circuits && {
        id: PanelsKeys.boiler_heating_circuits,
        component: (_jsx(BoilerHeatingCircuitsTemplate, { panel_key: PanelsKeys.boiler_heating_circuits, config: boiler_heating_circuits, hide_title: hide_panel_titles }, PanelsKeys.boiler_heating_circuits)),
    }, [hide_panel_titles]);
    const heating_modes_panel = useCallback((heating_modes) => heating_modes && {
        id: PanelsKeys.heating_modes,
        component: (_jsx(HeatingModesTemplate, { panel_key: PanelsKeys.heating_modes, config: heating_modes, hide_title: hide_panel_titles }, PanelsKeys.heating_modes)),
    }, [hide_panel_titles]);
    const boiler_heating_modes_panel = useCallback((boiler_heating_modes) => boiler_heating_modes && {
        id: PanelsKeys.boiler_heating_modes,
        component: (_jsx(HeatingModesTemplate, { panel_key: PanelsKeys.boiler_heating_modes, config: boiler_heating_modes, hide_title: hide_panel_titles }, PanelsKeys.boiler_heating_modes)),
    }, [hide_panel_titles]);
    const thermometers_panel = useCallback((thermometers) => thermometers && {
        id: PanelsKeys.thermometers,
        component: (_jsx(ThermometersTemplate, { panel_key: PanelsKeys.thermometers, config: thermometers, hide_title: hide_panel_titles }, PanelsKeys.thermometers)),
    }, [hide_panel_titles]);
    const sensors_panel = useCallback((sensors) => sensors && {
        id: PanelsKeys.sensors,
        component: (_jsx(SensorsTemplate, { panel_key: PanelsKeys.sensors, config: sensors, hide_title: hide_panel_titles }, PanelsKeys.sensors)),
    }, [hide_panel_titles]);
    const custom_controls_panel = useCallback((custom_controls) => is_climatic
        ? null
        : custom_controls && {
            id: PanelsKeys.custom_controls,
            component: (_jsx(CustomControlsTemplate, { panel_key: PanelsKeys.custom_controls, config: custom_controls, hide_title: hide_panel_titles }, PanelsKeys.custom_controls)),
        }, [is_climatic, hide_panel_titles]);
    const guard_control_panel = useCallback((guard_control) => guard_control && {
        id: PanelsKeys.guard_control,
        component: (_jsx(GuardControlTemplate, { panel_key: PanelsKeys.guard_control, config: guard_control, hide_title: hide_panel_titles }, PanelsKeys.guard_control)),
    }, [hide_panel_titles]);
    const rf_indicators_panel = useCallback((rf_indicators) => rf_indicators && {
        id: PanelsKeys.rf_indicators,
        component: (_jsx(RfIndicatorsTemplate, { panel_key: PanelsKeys.rf_indicators, config: rf_indicators }, PanelsKeys.rf_indicators)),
    }, []);
    const simple_view_panels = useCallback((heating_circuits) => [
        heating_circuits && {
            id: PanelsKeys.simple_view,
            component: (_jsx(SimpleViewPanel, { config: heating_circuits }, PanelsKeys.simple_view)),
        },
    ], []);
    const getUISettingsInLocalStorage = useCallback(() => {
        const getLocalStorage = localStorage.getItem(`device_ui_settings_${device_id}`);
        if (getLocalStorage) {
            return {
                ...JSON.parse(getLocalStorage),
                ...(initial_device_ui_settings.icons && {
                    icons: initial_device_ui_settings.icons,
                }),
            };
        }
        else {
            return window.PRELOAD.devices.find((device) => device.id === device_id)
                ?.ui_settings;
        }
    }, [device_id, initial_device_ui_settings]);
    const device_ui_settings = useMemo(() => getDeviceUiSettings(tab_key, full_device_config, {
        device_ui_settings: getUISettingsInLocalStorage(),
    }, is_support), [tab_key, initial_device_ui_settings, full_device_config]);
    const getCurrentPanelsConfig = useCallback((device_ui_settings) => getPanelsConfig(tab_key, full_device_config, {
        device_ui_settings,
        sorting_mode,
    }), [tab_key, full_device_config, sorting_mode]);
    const getFilteredPanels = useCallback((common_panels, device_ui_settings) => {
        const panels = common_panels
            .map((panel) => {
            const panel_ui_settings = device_ui_settings?.[tab_key]?.[panel.id];
            if (!panel_ui_settings) {
                return {
                    ...panel,
                    ui_settings: {},
                };
            }
            return { ...panel, ui_settings: { ...panel_ui_settings } };
        })
            .sort(sortByUISettings('position'));
        const filtered_panels = applyFilterUiSettings(panels, !sorting_mode);
        return filtered_panels;
    }, [sorting_mode]);
    const getTabConfig = useCallback((device_ui_settings) => {
        const panels_config = getCurrentPanelsConfig(device_ui_settings);
        const { heating_circuits, heating_modes, boiler_heating_modes, thermometers, guard_zones, vehicle_controls, boiler_heating_circuits, vehicle_custom_controls, rf_indicators, sensors, custom_controls, guard_controls, } = panels_config;
        const common_panels = removeNulls([
            ...(tab_custom_panels(panels_config, custom_panels) || []),
            heating_circuits_panel(heating_circuits),
            boiler_heating_circuits_panel(boiler_heating_circuits),
            custom_controls_panel(custom_controls),
            heating_modes_panel(heating_modes),
            boiler_heating_modes_panel(boiler_heating_modes),
            guard_control_panel(guard_controls),
            sensors_panel(sensors),
            vehicle_controls_panel(vehicle_controls, thermometers, vehicle_custom_controls),
            guard_zones_panel(guard_zones),
            thermometers_panel(thermometers),
            rf_indicators_panel(rf_indicators),
        ]);
        const filtered_panels = getFilteredPanels(common_panels, device_ui_settings);
        const panels = simple_view_mode
            ? simple_view_panels(heating_circuits)
            : filtered_panels;
        return { panels, device_ui_settings };
    }, [
        custom_panels,
        tab_key,
        full_device_config,
        simple_view_mode,
        getFilteredPanels,
        getCurrentPanelsConfig,
    ]);
    const current_tab_config = useMemo(() => {
        return {
            ...getTabConfig(device_ui_settings),
            panels_config: getCurrentPanelsConfig(device_ui_settings),
        };
    }, [device_ui_settings, getCurrentPanelsConfig, getTabConfig]);
    const [tab_config, setTabConfig] = useState(current_tab_config);
    const setUiSettings = useAction(set_ui_settings);
    useEffect(() => {
        setTabConfig(current_tab_config);
    }, [current_tab_config]);
    useEffect(() => () => {
        setUiSettings({ sorting_mode: false });
    }, []);
    useEffect(() => {
        if (!sorting_mode) {
            setIsDeviceUiSettingsCancel();
        }
    }, [sorting_mode]);
    useEffect(() => {
        if (Object.keys(custom_panels).length === 0) {
            setUiSettings({ grouping_mode: false });
        }
    }, [custom_panels]);
    const updateDevice = useAction(requestDeviceUpdate);
    const is_owner = useMemo(() => user_id === owner_user_id, [user_id, owner_user_id]);
    const onSaveDeviceUiSettings = useCallback(() => {
        if (!is_owner) {
            const new_set = Object.keys(tab_config.device_ui_settings).reduce((acc, key) => {
                if (key !== 'icons' && key !== 'hidden_name') {
                    const new_key = `${key}_access_${user_id}`;
                    return {
                        ...acc,
                        [new_key]: tab_config.device_ui_settings[key],
                    };
                }
                else {
                    return {
                        ...acc,
                        [key]: tab_config.device_ui_settings[key],
                    };
                }
            }, {});
            updateDevice(device_id, {
                ui_settings: new_set,
            });
        }
        else {
            updateDevice(device_id, {
                ui_settings: tab_config.device_ui_settings,
            });
        }
        const uiSettingsForLocalStorage = lodash.omit(tab_config.device_ui_settings, 'icons');
        saveToLocalStorage(`device_ui_settings_${device_id}`, uiSettingsForLocalStorage);
        setUiSettings({ sorting_mode: false });
    }, [device_id, tab_config.device_ui_settings, user_id]);
    const contentContext = useMemo(() => ({
        grouping_mode,
        sorting_mode,
        simple_view_mode,
        is_device_ui_settings_changed,
        panels: tab_config.panels,
        dashboard_sorting_mode,
        onSaveDeviceUiSettings,
    }), [
        grouping_mode,
        sorting_mode,
        simple_view_mode,
        dashboard_sorting_mode,
        onSaveDeviceUiSettings,
        tab_config.panels,
        is_device_ui_settings_changed,
    ]);
    return (_jsx(DeviceUISettingsContent.Provider, { value: contentContext, children: _jsx(DeviceUISettingsControlProvider, { setIsDeviceUiSettingsChanged: setIsDeviceUiSettingsChanged, setIsDeviceUiSettingsCancel: setIsDeviceUiSettingsCancel, custom_panels: custom_panels, setCustomPanels: setCustomPanels, getTabConfig: getTabConfig, tab_config: tab_config, setTabConfig: setTabConfig, sorting_mode: sorting_mode, device_ui_settings: device_ui_settings, templates: templates, children: children }) }));
});
export const useDeviceUISettings = () => useContext(DeviceUISettingsContent);
